import React from 'react';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { v4 as uuidv4 } from 'uuid';
import { useIntl, FormattedMessage } from 'react-intl';

import RichText from '../RichText';
import TextLink from '../TextLink/TextLink';
import isKiosk from '../../../utils/isKiosk';
import { ContentBody, Heading } from '../../../ContentfulTypes';

import {
  LegalContainer,
  LegalWrapper,
  StyledAccordionItem,
  StyledAccordionArrow,
  StyledAccordionItemTitle,
  StyledAccordionItemButton,
  Title,
  StyledAccordionBody,
} from './styles';

interface Props {
  preExpand: boolean;
  legalText?: Array<ContentBody | null | undefined>;
  legalHeading?: Heading | null;
  pageId?: string;
  className?: string;
}

const legalHeadingOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Title className="u-position-relative">{children}</Title>
    ),
  },
};

// Elements to render on rich text
const legalTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
    [INLINES.HYPERLINK]: (
      node,
      children,
      { locale } = useIntl(),
    ) => node?.data?.uri.startsWith('/') ? (
      <a href={`/${locale}${node?.data?.uri}`}>{children}</a>
    ) : (
      <a
        href={`${node?.data?.uri}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    [INLINES.ENTRY_HYPERLINK]: (entry) => (
      <a
        href={entry.data?.target?.asset?.localImage.publicURL}
        target="_blank"
        rel="noopener noreferrer"
      >
        {entry.content[0].value}
      </a>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node, { locale } = useIntl()) => (
      <TextLink
        route={node?.data?.target?.ctaTarget}
        url={node?.data?.target?.ctaUrl}
        linkText={node?.data?.target?.ctaLabel?.ctaLabel}
        linkColor={node?.data?.target?.ctaColor}
        linkSize="medium"
        locale={locale}
      />
    ),
  },
};

// Remove all external hyperlinks in KioskMode
const legalTextOptionsKiosk = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
    [INLINES.HYPERLINK]: (
      node,
      children,
      { locale } = useIntl(),
    ) => node?.data?.uri.startsWith('/') ? (
      <a href={`/${locale}${node?.data?.uri}`}>{children}</a>
    ) : (
      children
    ),
    [INLINES.ENTRY_HYPERLINK]: (entry) => (
      entry.content[0].value
    ),
    [INLINES.EMBEDDED_ENTRY]: (
      node,
      { locale } = useIntl(),
    ) => node?.data?.target?.ctaUrl.startsWith('/') ? (
      <TextLink
        route={node?.data?.target?.ctaTarget}
        url={node?.data?.target?.ctaUrl}
        linkText={node?.data?.target?.ctaLabel?.ctaLabel}
        linkColor={node?.data?.target?.ctaColor}
        linkSize="medium"
        locale={locale}
      />
    ) : (
      node?.data?.target?.ctaLabel?.ctaLabel
    ),
  },
};

const LegalText = ({
  preExpand,
  legalText,
  legalHeading,
  pageId,
  className,
}: Props) => {
  const preExpandList = preExpand ? ['0'] : [];
  const isKioskMode = isKiosk();

  return (
    <LegalContainer pageId={pageId} className={`legalContainer ${className}`} data-testid="legal-container-section">
      <LegalWrapper
        allowMultipleExpanded
        allowZeroExpanded
        preExpanded={preExpandList}
      >
        <StyledAccordionItem uuid="0">
          <StyledAccordionItemTitle>
            <StyledAccordionItemButton>
              {legalHeading ? (
                <RichText
                  document={legalHeading}
                  options={legalHeadingOptions}
                />
              ) : (
                <Title className="u-position-relative">
                  <FormattedMessage id="LegalDetails" />
                </Title>
              )}

              <StyledAccordionArrow />
            </StyledAccordionItemButton>
          </StyledAccordionItemTitle>
          {legalText?.map((text) => (
            <StyledAccordionBody key={uuidv4()}>
              <RichText
                document={text}
                options={isKioskMode ? legalTextOptionsKiosk : legalTextOptions}
              />
            </StyledAccordionBody>
          ))}
        </StyledAccordionItem>
      </LegalWrapper>
    </LegalContainer>
  );
};

export default LegalText;
